import * as Sentry from '@sentry/react';
import type { History } from 'history';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import {
  applyMiddleware, combineReducers, compose, createStore
} from '@reduxjs/toolkit';
import thunk from 'redux-thunk';
import type { RouterState } from 'connected-react-router';
import type { IAuditState } from '~modules/Audit/interfaces';
import type { IAuthState } from '~modules/Auth/interfaces';
import type { ICompanyState } from '~modules/Companies/interfaces';
import type { IFeatureFlagsModuleState } from '~modules/FeatureFlags/interfaces';
import type { IIdentificationState } from '~modules/Identification/interfaces';
import type { ILicenseState } from '~modules/Licenses/interfaces';
import type { IProductSettingsState } from '~modules/ProductSettings/interfaces';
import type { IReleaseNotesModuleState } from '~modules/ReleaseNotes/interfaces';
import type { IStatsState } from '~modules/Statistics/interfaces';
import type { ISubjectState } from '~modules/Subject/interfaces';
import type { ISurveysModuleState } from '~modules/Surveys/interfaces';
import type { IUserState } from '~modules/User/interfaces';
import type { IUsersState } from '~modules/Users/interfaces';
import auditReducer from '~modules/Audit/reducers';
import authReducer from '~modules/Auth/reducer';
import companyReducer from '~modules/Companies/reducer';
import identificationReducer from '~modules/Identification/reducers';
import licenseReducer from '~modules/Licenses/reducer';
import statsReducer from '~modules/Statistics/reducer';
import subjectReducer from '~modules/Subject/reducer';
import featureFlagsReducer from '~modules/FeatureFlags/reducer';
import userReducer from '~modules/User/reducers';
import usersReducer from '~modules/Users/reducers';
import productSettingsReducer from '~modules/ProductSettings/reducer';
import surveyReducer from "~modules/Surveys/reducers";
import releaseNotesReducer from "~modules/ReleaseNotes/reducers";

const defaultReducers = {
  productSettingsReducer,
  companyReducer,
  identificationReducer,
  auditReducer,
  usersReducer,
  userReducer,
  licenseReducer,
  statsReducer,
  authReducer,
  subjectReducer,
  featureFlagsReducer,
  surveyReducer,
  releaseNotesReducer
};

export interface RootState {
  router: RouterState;
  productSettingsReducer: IProductSettingsState;
  companyReducer: ICompanyState;
  identificationReducer: IIdentificationState;
  auditReducer: IAuditState;
  usersReducer: IUsersState;
  userReducer: IUserState;
  licenseReducer: ILicenseState;
  statsReducer: IStatsState;
  authReducer: IAuthState;
  subjectReducer: ISubjectState;
  featureFlagsReducer: IFeatureFlagsModuleState;
  surveyReducer: ISurveysModuleState;
  releaseNotesReducer: IReleaseNotesModuleState;
}

export const rootReducer = (history: History) => combineReducers<RootState>({
  router: connectRouter(history),
  ...defaultReducers
});

const composeEnhancers = (typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

export const makeStore = (history: History) => createStore(
  rootReducer(history),
  composeEnhancers(applyMiddleware(thunk, routerMiddleware(history)), sentryReduxEnhancer)
);

export type TStore = ReturnType<typeof makeStore>;
export type AppDispatch = TStore['dispatch'];
